/**
 * External Dependencies
 */
import React, { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

/**
 * Internal Dependencies
 */
import LoadingAnimation from '../../components/loading-animation';
import { completeLogin } from '../../utils/oidcAuth';

// OIDC redirect-callback landing page for www's silent warm-login.
//
// auth-v2 sends the browser here as `/oidc/callback?code=...&state=...` after the
// warm /authorize leg (see utils/oidcAuth.js — mayar-www is registered against
// `${origin}/oidc/callback`, NOT /auth, which traefik routes to the auth-gateway).
// On mount we exchange the code (+ PKCE verifier) for the
// Mayar token pair, which completeLogin() writes into the existing `token`
// cookie. We then hand off to www's normal post-login bootstrap via the
// `#token=` gate in App.jsx: a full-page load makes PageWrap read the hash
// token, dispatch updateAuth, and run maybeCheckAuth into the dashboard.
//
// On error=login_required (cold Mayar session), an OIDC error, a state/PKCE
// mismatch, or a failed token exchange we fall through to /sign-in and show the
// normal login page. There is no redirect loop: this handler never (re)starts
// the silent probe — that lives on /sign-in and is bounded by
// MAX_SILENT_ATTEMPTS in oidcAuth.js.

// Cold miss (no warm Mayar session): tag the bounce so /sign-in renders the
// guest login page and does NOT immediately re-probe — this one marker breaks
// the invisible /authorize -> /auth -> /sign-in loop without a cooldown.
const goSignInColdMiss = () => window.location.replace('/sign-in?sso=none');
// Exchange failure AFTER a warm code was issued: bounce plain so the counter is
// NOT reset, letting MAX_SILENT_ATTEMPTS cap a warm-but-unusable loop.
const goSignIn = () => window.location.replace('/sign-in');

const AuthCallback = () => {
  // One-shot guard so the single-use authorization code is exchanged exactly
  // once per instance. NB: this is INERT on www's React 16 — a [] effect fires
  // once per mount and StrictMode double-invokes render, not effects, so
  // `started.current` is never read true today. What actually de-dupes here is
  // completeLogin()'s module-scope pendingExchange (see utils/oidcAuth.js).
  // Kept because it costs nothing and becomes load-bearing the moment www moves
  // to React 18, whose StrictMode DOES double-fire effects in dev — which would
  // otherwise re-read the params and navigate twice. Do not write a test that
  // claims to exercise it under React 16: it will pass with the guard deleted.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    // Already authenticated — nothing to exchange, just land in-app.
    if (Cookies.get('token')) {
      // eslint-disable-next-line no-console
      console.warn('[AuthCallback] already have token cookie, redirecting to /');
      window.location.replace('/');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    // Cold session (login_required) or any explicit OIDC error, or a missing
    // code — nothing to exchange. Bounce as a cold miss so /sign-in shows the
    // guest page and can retry warm login on a later clean reload.
    if (error || !code) {
      goSignInColdMiss();
      return;
    }

    completeLogin({ code, state })
      .then(tokenPair => {
        // eslint-disable-next-line no-console
        console.warn('[AuthCallback] completeLogin success, navigating to /#token=%s...', tokenPair?.substring(0, 20));
        // Reuse www's existing post-login handoff: a full-page navigation with
        // the fresh pair on `#token=` re-runs App bootstrap (updateAuth +
        // maybeCheckAuth) and lands the user in the dashboard. The `token`
        // cookie is already set by completeLogin().
        window.location.replace(`/#token=${tokenPair}`);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn('[AuthCallback] completeLogin failed:', err?.message || err);
        // State/PKCE desync or token exchange failure — terminal. Fall through
        // to /sign-in (no loop).
        goSignIn();
      });
  }, []);

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <LoadingAnimation mayar />
    </div>
  );
};

export default AuthCallback;
